import { useMemo } from 'react'

/**
 * The shared shape behind every admin CRUD hook: one `list` query plus
 * create/update/delete mutations that all invalidate the same queries, with
 * the query error flattened to a message string.
 *
 * Domain extras (validation, reordering, link/unlink procedures) are not
 * modelled here — declare them next to the `useResource` call in the hook that
 * needs them.
 */

/** The subset of a tRPC query result these hooks read. */
export interface ResourceQueryResult<TData> {
  data: TData | undefined
  isLoading: boolean
  error: { message: string } | null
}

/** The subset of a tRPC mutation procedure these hooks call. */
export interface ResourceMutationProcedure<TInput, TOutput> {
  useMutation: (options: { onSuccess?: (data: TOutput) => void }) => {
    mutateAsync: (input: TInput) => Promise<TOutput>
  }
}

export interface UseResourceListConfig<TData, TItem> {
  /** The already-invoked list query, e.g. `trpc.admin.companies.list.useQuery()`. */
  query: ResourceQueryResult<TData>
  /**
   * Pulls the rows out of the list payload. Re-runs when `query.data` or the
   * function's own identity changes, so an unmemoized `select` recomputes every
   * render and hands back a fresh array. Memoize it when downstream identity
   * matters — and then list every value it closes over, or the derived rows go
   * stale (see the `type` dependency in `useStudyPlans`).
   */
  select: (data: TData | undefined) => TItem[]
  /**
   * Invalidates every query a write to this resource affects. Must be stable
   * (`useCallback`) — it is the mutations' `onSuccess`. Return the invalidation
   * promise if callers need to await the refetch.
   */
  invalidate: () => void | Promise<unknown>
}

export interface UseResourceListResult<TData, TItem> {
  items: TItem[]
  /** The raw list payload, for callers that need more than the rows. */
  data: TData | undefined
  loading: boolean
  /** The query's failure message, or null. */
  error: string | null
  refresh: () => void | Promise<unknown>
}

/** The list half of {@link useResource}, for read-mostly resources. */
export function useResourceList<TData, TItem>({
  query,
  select,
  invalidate,
}: UseResourceListConfig<TData, TItem>): UseResourceListResult<TData, TItem> {
  const items = useMemo(() => select(query.data), [query.data, select])

  return {
    items,
    data: query.data,
    loading: query.isLoading,
    error: query.error?.message ?? null,
    refresh: invalidate,
  }
}

export interface UseResourceMutationsConfig<
  TCreateInput,
  TCreateOutput,
  TUpdateInput,
  TUpdateOutput,
  TDeleteInput,
  TDeleteOutput,
> {
  create: ResourceMutationProcedure<TCreateInput, TCreateOutput>
  update: ResourceMutationProcedure<TUpdateInput, TUpdateOutput>
  remove: ResourceMutationProcedure<TDeleteInput, TDeleteOutput>
  /** Runs after every successful write. Must be stable (`useCallback`). */
  invalidate: () => void | Promise<unknown>
}

export interface UseResourceMutationsResult<
  TCreateInput,
  TCreateOutput,
  TUpdateInput,
  TUpdateOutput,
  TDeleteInput,
  TDeleteOutput,
> {
  create: (input: TCreateInput) => Promise<TCreateOutput>
  update: (input: TUpdateInput) => Promise<TUpdateOutput>
  remove: (input: TDeleteInput) => Promise<TDeleteOutput>
}

/**
 * The mutation half of {@link useResource}, for resources whose list is not a
 * plain query (e.g. paginated with `useInfiniteQuery`).
 */
export function useResourceMutations<
  TCreateInput,
  TCreateOutput,
  TUpdateInput,
  TUpdateOutput,
  TDeleteInput,
  TDeleteOutput,
>({
  create,
  update,
  remove,
  invalidate,
}: UseResourceMutationsConfig<
  TCreateInput,
  TCreateOutput,
  TUpdateInput,
  TUpdateOutput,
  TDeleteInput,
  TDeleteOutput
>): UseResourceMutationsResult<
  TCreateInput,
  TCreateOutput,
  TUpdateInput,
  TUpdateOutput,
  TDeleteInput,
  TDeleteOutput
> {
  const createMutation = create.useMutation({ onSuccess: invalidate })
  const updateMutation = update.useMutation({ onSuccess: invalidate })
  const deleteMutation = remove.useMutation({ onSuccess: invalidate })

  return {
    create: createMutation.mutateAsync,
    update: updateMutation.mutateAsync,
    remove: deleteMutation.mutateAsync,
  }
}

export type UseResourceConfig<
  TData,
  TItem,
  TCreateInput,
  TCreateOutput,
  TUpdateInput,
  TUpdateOutput,
  TDeleteInput,
  TDeleteOutput,
> = UseResourceListConfig<TData, TItem> &
  UseResourceMutationsConfig<
    TCreateInput,
    TCreateOutput,
    TUpdateInput,
    TUpdateOutput,
    TDeleteInput,
    TDeleteOutput
  >

/** List + create/update/delete for one admin resource. */
export function useResource<
  TData,
  TItem,
  TCreateInput,
  TCreateOutput,
  TUpdateInput,
  TUpdateOutput,
  TDeleteInput,
  TDeleteOutput,
>({
  query,
  select,
  invalidate,
  create,
  update,
  remove,
}: UseResourceConfig<
  TData,
  TItem,
  TCreateInput,
  TCreateOutput,
  TUpdateInput,
  TUpdateOutput,
  TDeleteInput,
  TDeleteOutput
>): UseResourceListResult<TData, TItem> &
  UseResourceMutationsResult<
    TCreateInput,
    TCreateOutput,
    TUpdateInput,
    TUpdateOutput,
    TDeleteInput,
    TDeleteOutput
  > {
  const list = useResourceList({ query, select, invalidate })
  const mutations = useResourceMutations({
    create,
    update,
    remove,
    invalidate,
  })

  return { ...list, ...mutations }
}
